import React from 'react';
import { Box, HStack, IBoxProps, Skeleton, VStack } from 'native-base';
import { resizedUrl, Reward, RewardGrant } from '@springbig/shared';
import { Caption, H3 } from './Typography';
import Image from './Image';
import { SpIcon } from './Icon';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button, Panel, Card } from '~/shared/Theme';
import { useTheme } from '@springbig/shared';
import { DarkSkeleton } from './DarkSkeleton';

type DesiredAction = {
    id: number;
    name: string;
    rewards: Reward[];
    type_id: number;
};

type RewardProps = {
  offer?: RewardGrant
  handleAcceptRedeemReward?: (id: number, title: string) => void;
  handleBonusRewards?: (id: number) => void;
  rewardActiveId?: number | undefined;
  loadingActivation?: boolean;
  titleIcon: IconProp;
  iconTitle: string;
  leftIcon: IconProp;
  bodyText: string | undefined;
  subTitle: string;
};

type DesiredActionProps = {
  desiredAction? : DesiredAction;
  onGetOffer?: (typeId: number) => void;
  titleIcon: IconProp;
  iconTitle: string;
  leftIcon: IconProp;
  bodyText: string | undefined;
  subTitle: string;
};

export interface RewardGrantCardProps extends IBoxProps {
  prop: {
    reward?: RewardProps
    desiredAction?: DesiredActionProps
  }
  type: 'reward' | 'desiredAction'
}

const RewardGrantCard = ({
    prop,
    type
}: RewardGrantCardProps) => {

    const {
        primary,
        radius_md,
    } = useTheme();

    return (
        <Panel alignItems={'center'} mb={4}>
            <HStack space={2}>
                { prop[type]?.titleIcon &&
                    <SpIcon color={primary} icon={prop[type]?.titleIcon} />
                }
                <Caption
                    fontWeight="bold"
                    textAlign="center"
                    textTransform="capitalize"
                    color={primary}
                >
                    { prop[type]?.iconTitle.replace('_', ' ') }
                </Caption>
            </HStack>
            <HStack
                space={2}
                w={'100%'}
                alignItems={'center'}
                justifyContent={'space-evenly'}
            >
                { prop[type] && type === 'reward' && prop[type].offer?.reward && prop[type].offer?.reward?.images?.length > 0 ?
                    <Card secondary p={0}>
                        <Image
                            w={'112px'}
                            h={'112px'}
                            borderRadius={radius_md}
                            resizeMode={'cover'}
                            uri={resizedUrl(prop[type].offer?.reward?.images[0].url, 112, 112)}
                            useSkeletonLoader={true}
                            skeletonH={'112px'}
                            skeletonW={'112px'}
                        />
                    </Card>
                    :
                    prop[type]?.leftIcon &&
                        <Card secondary>
                            <SpIcon color={primary} icon={prop[type]?.leftIcon} size={80} />
                        </Card>
                }
                <Box width={'45%'} alignSelf={'center'}>
                    <H3
                        textAlign="center"
                        textOverflow={'ellipsis'}
                        numberOfLines={2}
                    >
                        { prop[type]?.bodyText }
                    </H3>
                    <Caption textAlign={'center'}>
                        { prop[type]?.subTitle }
                    </Caption>
                </Box>
                { type === 'reward' && (
                    <VStack w={'20%'}>
                        { prop[type]?.offer?._type === 'bonus' ? (
                            <Button
                                isDisabled={
                                    prop[type].offer?.redemptions_available === 0 || prop[type].loadingActivation || prop[type].offer?.active
                                }
                                onPress={() => {

                                    if (prop[type]?.offer?.active || prop[type]?.loadingActivation) {
                                        return;
                                    }

                                    if (prop[type]?.handleBonusRewards) {
                                        prop[type].handleBonusRewards(prop[type]?.offer?.id!);
                                    }
                                }}
                                text={prop[type].offer.active ? 'Earning' : 'Use'}
                            />
                        ) : (
                            <Button
                                h={50}
                                text={'general.get'}
                                disabled={prop[type]?.offer?.redemptions_available === 0}
                                onPress={() => {

                                    if (prop[type]?.handleAcceptRedeemReward) {
                                        prop[type].handleAcceptRedeemReward(prop[type]?.offer?.id!, prop[type]?.offer?.reward.name);
                                    }
                                } }
                            />
                        )}
                        <Box>
                            { prop[type]?.offer?.redemptions_available === 0 ? (
                                <Caption
                                    textAlign="center"
                                    text={'general.redeemed'}
                                />
                            ) : (
                                <Caption textAlign="center">
                                    { prop[type]?.offer?.redemptions_available === -1
                                        ? 'unlimited'
                                        : `use ${prop[type]?.offer?.redemptions_available}x`}
                                </Caption>
                            )}
                        </Box>
                    </VStack>
                )}
                { type === 'desiredAction' &&
                    <Button
                        h={50}
                        text={'offers.add'}
                        onPress={() => {

                            if (prop[type]?.onGetOffer && prop[type].desiredAction?.type_id) {
                                prop[type]?.onGetOffer(prop[type]?.desiredAction?.type_id);
                            }
                        }}
                    />
                }
            </HStack>
            { type === 'desiredAction' && (
                <Caption
                    textAlign="center"
                    text="offers.desirable_action"
                    textValues={[
                        prop[type]?.bodyText ?? '',
                        `${prop[type]?.desiredAction?.rewards[0]?.name ?? ''}`,
                    ]}
                />
            )}
        </Panel>
    );
};

export const RewardGrantCardSkeleton = () => {

    return (
        <Panel space={2}>
            <HStack space={2} alignItems={'center'} justifyContent={'center'}>
                <DarkSkeleton h={'24px'} w={'30%'} />
            </HStack>
            <HStack
                space={2}
                w={'100%'}
                alignItems={'center'}
                justifyContent={'space-evenly'}
            >
                <DarkSkeleton h={24} w={24} />
                <VStack space={'2px'} width={'45%'} alignSelf={'center'}>
                    <DarkSkeleton h={'22px'} />
                    <DarkSkeleton h={'22px'} w={'80%'} mx={'auto'} />
                </VStack>
                <VStack space={'2px'} width={'20%'} alignSelf={'center'}>
                    <DarkSkeleton h={'48px'} />
                    <DarkSkeleton h={'22px'} w={'80%'} mx={'auto'} />
                </VStack>
            </HStack>
        </Panel>
    );
};

export default RewardGrantCard;
